import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../assets/images";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import PayPalPackages from "../components/PayPalPackages";

const trackImages = [IMAGES.creativeTraining, IMAGES.womenStrength, IMAGES.execTraining];

export default function Coaching() {
  const { t } = useLanguage();
  const c = t.coachingPage;

  return (
    <>
      <Seo title={c.seoTitle} description={c.seoDesc} />

      {/* INTRO */}
      <section className="pt-16 pb-20 md:py-28 border-b border-line-subtle relative overflow-hidden" data-testid="coaching-page-hero">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-gold/5 blur-3xl pointer-events-none d1-drift" />
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">{c.eyebrow}</span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white mt-2 mb-8" data-testid="coaching-page-title">
              <span className="ln"><span>{c.title}</span></span>
            </h1>
            <p className="text-lg text-bone-soft leading-relaxed max-w-xl d1-reveal">{c.lead}</p>
          </div>
          <Reveal className="md:col-span-5" delay={150}>
            <figure className="relative aspect-[4/3] rounded-lg border border-line-strong overflow-hidden">
              <img
                src={IMAGES.womenStrength}
                alt={c.pillars[1].title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="coaching-hero-img"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </figure>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-coal-raise py-24 border-b border-line-subtle" data-testid="coaching-pillars">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">{c.pillarsEyebrow}</span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
              {c.pillarsTitle}
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div
                  className="bg-coal-hover p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full"
                  data-testid={`pillar-card-${i + 1}`}
                >
                  <div className="text-xs font-mono uppercase text-gold mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-xl uppercase text-white mb-3">{p.title}</h3>
                  <p className="text-sm text-bone-soft leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRACKS */}
      <section className="py-24 border-b border-line-subtle" data-testid="coaching-tracks">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">{c.tracksEyebrow}</span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
              {c.tracksTitle}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {c.tracks.map((track, i) => (
              <Reveal key={track.title} delay={i * 120}>
                <div
                  className="bg-coal-card p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full"
                  data-testid={`track-card-${i + 1}`}
                >
                  <figure className="relative aspect-[16/9] rounded border border-line-strong overflow-hidden mb-6">
                    <img
                      src={trackImages[i]}
                      alt={track.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </figure>
                  <h3 className="font-display text-2xl uppercase text-white mb-3">{track.title}</h3>
                  <p className="text-sm text-bone-soft leading-relaxed">{track.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="bg-coal-raise py-24 border-b border-line-subtle" data-testid="coaching-formats">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">{c.formatsEyebrow}</span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
              {c.formatsTitle}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {c.formats.map((f, i) => (
              <Reveal key={f.title} delay={i * 120}>
                <div
                  className="bg-coal-hover p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full"
                  data-testid={`format-card-${i + 1}`}
                >
                  <h3 className="font-display text-2xl uppercase text-white mb-3">{f.title}</h3>
                  <p className="text-sm text-bone-soft leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRAINING PACKAGES (PayPal) */}
      <PayPalPackages />

      {/* CTA */}
      <section className="py-24" data-testid="coaching-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display uppercase text-3xl sm:text-5xl tracking-tight text-white mb-4">
              {c.ctaTitle}
            </h2>
            <p className="text-sm text-bone-soft max-w-lg mx-auto mb-10">{c.ctaBody}</p>
            <Link
              to="/#intake"
              className="inline-flex justify-center items-center px-7 py-4 text-sm font-semibold tracking-wider uppercase bg-gold text-black rounded hover:bg-gold-bright transition-colors"
              data-testid="coaching-cta-button"
            >
              {c.ctaButton}
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
